import React, { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';
import { FlexColumn, Gap } from 'ui-components/components/flex';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { Text } from 'ui-components/components/text/text';
import { TextType } from 'ui-components/components/text/text-type';

import { InsurancePreferenceRadio } from './components/insurance-preference-radio/insurance-preference-radio';
import { InsurancePromoCard } from './components/insurance-promo-card/insurance-promo-card';
import { insurancePromoCardMessages } from './components/insurance-promo-card/messages';
import { messages, parcelGuardMessages, xCoverMessages } from './messages';
import commonStyles from '../../settings-page.module.less';
import styles from './walleted-insurance.module.less';
import { WalletedInsuranceLegacy } from './walleted-insurance-legacy';
import { WhyInsuranceCard } from './why-insurance-card';
import { WalletBalanceInfo } from '../../../../components/wallet-balance-info';
import { INSURANCE_SETTINGS_VIEWED, INSURANCE_OPTION_UPDATED } from '../../../../constants/analytics-events';
import { InsuranceProviders, getInsuranceProviderById } from '../../../../enums/insurance-providers';
import { useShowInsurancePromoExtendedUi } from '../../../../feature-flags/hooks/use-show-insurance-promo-extended-ui';
import { useWalletedInsuranceActivated, useWalletedInsuranceProviderId } from '../../../../services/insurance-service';
import { useSystemAutomation } from '../../../../contexts/system-automation-context';
import { SYSTEM_RULE_NAMES } from '../../../../contexts/system-automation-context/constants';
import { INSURANCE_PREFERENCE, saveInsurancePreference, useInsurancePreference } from '../../../../hooks/use-insurance-preference';
import { useHasParcelGuardAutomationRule } from '../../../../hooks/use-has-parcel-guard-automation-rule';
import { Segment } from '../../../../services/analytics-services/segment';
import { saveNewSellerSettings } from '../../../../store/seller/seller.actions';
import { useTrackInsuranceEvent } from '../../../../insurance/hooks/use-track-insurance-event';
import { EventSourceStrings } from '../../../../tracking/tracking-constants';
import SaveBar from '../../components/save-bar';
import { SettingsPageTitle } from '../../components/settings-page-title/settings-page-title';
import { WalletRegistrationOrUpdateButton } from '../../../../wallet/components/wallet-registration-or-update-button';

/** @typedef {import('../../../../hooks/use-insurance-preference').InsurancePreference} InsurancePreference */

const INSURANCE_SETTINGS_PAGE = 'Insurance Settings';

/**
 * @param {object} props
 * @param {() => Promise<unknown>} [props.onSettingsSave]
 * @returns {JSX.Element}
 */
export const WalletedInsuranceSettings = ({ onSettingsSave }) => {
  const showInsurancePromoExtendedUi = useShowInsurancePromoExtendedUi();
  const dispatch = useDispatch();
  const intl = useIntl();

  const insuranceProviderId = useWalletedInsuranceProviderId();
  const isInsuranceActivated = useWalletedInsuranceActivated();

  const { getSystemRule, refreshAutomationRules } = useSystemAutomation();
  const hasParcelGuardAutomationRule = useHasParcelGuardAutomationRule();
  const { insurancePreference, isLoading: isPreferenceLoading, hasExplicitNonePreference } = useInsurancePreference();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  /** @type {[InsurancePreference | null, (value: InsurancePreference | null) => void]} */
  const [selectedPreference, setSelectedPreference] = useState(null);
  const [hasValidationError, setHasValidationError] = useState(false);

  const hasActiveInsuranceRule = getSystemRule(SYSTEM_RULE_NAMES.INSURE_BY_RULES)?.active ?? false;

  const hasNoCoveragePreference =
    isInsuranceActivated &&
    !isPreferenceLoading &&
    insurancePreference === INSURANCE_PREFERENCE.NONE &&
    !hasExplicitNonePreference &&
    !!insuranceProviderId;

  const handlePreferenceChange = useCallback((/** @type {InsurancePreference} */ preference) => {
    setSelectedPreference(preference);
    setHasUnsavedChanges(true);
    if (preference !== INSURANCE_PREFERENCE.INSURE_BY_RULES) {
      setHasValidationError(false);
    }
  }, []);

  const handleSave = useCallback(async () => {
    if (!selectedPreference) return;

    const currentRule = getSystemRule(SYSTEM_RULE_NAMES.INSURE_BY_RULES);
    const isRuleActive = currentRule?.active ?? false;

    if (selectedPreference === INSURANCE_PREFERENCE.INSURE_BY_RULES && !isRuleActive) {
      setHasValidationError(true);
      return;
    }
    setHasValidationError(false);

    try {
      const newSettings = await saveInsurancePreference({
        preference: selectedPreference,
        insuranceProviderId,
        existingSystemRule: currentRule,
        refreshAutomationRules,
        shouldWritePreferenceNone: true,
      });
      dispatch(saveNewSellerSettings(newSettings));

      setHasUnsavedChanges(false);
      Segment.trackEvent(INSURANCE_OPTION_UPDATED, {
        page: INSURANCE_SETTINGS_PAGE,
        optionSelected: selectedPreference,
      });

      if (onSettingsSave) {
        try {
          await onSettingsSave();
        } catch {
          // ignore errors, the only use case is already logged
        }
      }
    } catch {
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        content: intl.formatMessage(messages.savePreferencesError),
      });
    }
  }, [dispatch, intl, selectedPreference, insuranceProviderId, getSystemRule, onSettingsSave, refreshAutomationRules]);

  // ParcelGuard fallback is safe only because the early return below sends sellers without a walleted
  // provider down the legacy path. Keep the guard ordering intact if refactoring.
  const effectiveInsuranceProviderId = insuranceProviderId ?? InsuranceProviders.ParcelGuard.insuranceProviderId;
  const carrierMessages =
    effectiveInsuranceProviderId === InsuranceProviders.XCover.insuranceProviderId ? xCoverMessages : parcelGuardMessages;

  const trackSettingsViewed = useTrackInsuranceEvent(INSURANCE_SETTINGS_VIEWED, EventSourceStrings.INSURANCE_SETTINGS);
  const insuranceProviderName = getInsuranceProviderById(insuranceProviderId)?.name;

  const hasTrackedSettingsViewedRef = useRef(false);
  if (insuranceProviderId && !hasTrackedSettingsViewedRef.current) {
    hasTrackedSettingsViewedRef.current = true;
    trackSettingsViewed({ insuranceProvider: insuranceProviderName });
  }

  if (!showInsurancePromoExtendedUi) {
    return <WalletedInsuranceLegacy />;
  }

  return (
    <>
      <div className={commonStyles.content}>
        <div className={styles.container}>
          <div className={styles['title-content']}>
            <SettingsPageTitle value={messages.title} />
            <Text className={styles.description} textType={TextType.Description} value={insurancePromoCardMessages.pageDescription} />
          </div>
          <div className={styles['two-column-layout']}>
            <div className={styles['left-column']}>
              <div className={styles['insurance-promo-wrapper']}>
                <InsurancePromoCard insuranceProviderId={effectiveInsuranceProviderId} />
                {isInsuranceActivated ? (
                  <InsurancePreferenceRadio
                    hasActiveInsuranceRule={hasActiveInsuranceRule}
                    hasParcelGuardAutomationRule={hasParcelGuardAutomationRule}
                    hasValidationError={hasValidationError}
                    hasNoCoveragePreference={hasNoCoveragePreference}
                    onPreferenceChange={handlePreferenceChange}
                  />
                ) : (
                  <div className={styles['activate-insurance-section']}>
                    <WalletRegistrationOrUpdateButton trackingId={EventSourceStrings.INSURANCE_SETTINGS_CONTENT}>
                      {({ openModal }) => (
                        <Button className={styles['activate-insurance-button']} onClick={openModal} theme={THEME_CONSTANTS.primary}>
                          <Text textType={TextType.Label} value={carrierMessages.addInsuranceButton} />
                        </Button>
                      )}
                    </WalletRegistrationOrUpdateButton>
                  </div>
                )}
              </div>
            </div>
            <FlexColumn className={styles['right-column']} gap={Gap.Space24}>
              <WalletBalanceInfo isFullSize trackingId={EventSourceStrings.INSURANCE_SETTINGS} />
              {!isInsuranceActivated && <WhyInsuranceCard insuranceProviderId={effectiveInsuranceProviderId} />}
            </FlexColumn>
          </div>
        </div>
      </div>
      {isInsuranceActivated && <SaveBar disabled={!hasUnsavedChanges} onSave={handleSave} />}
    </>
  );
};
